import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import styled from '@emotion/styled';
import React, { LegacyRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDesktopMediaQuery } from '../../hooks';
import { ProductPlan } from '../../models';
import { usePriceFinderStore } from '../../store';
import {
  DefaultPlanLimits,
  deviceMediaQueries,
  SpecialPlanNames,
  TRACKING_EVENT_NAMES
} from '../../utils';
import { CTAButton } from '../cta-button';
import { ModalRedirectConfirm } from '../modal-redirect-confirm';
import { PlanCard } from './plan-card';
import { PlanCardBody } from './plan-card-body';

type PlanCardsContainerProps = {
  productKey: string;
  productPlans: ProductPlan[];
  recommendedPlanName: string;
  signupURL: string;
  contactURL: string;
  currencyISOCode: string;
  fontSize: string;
  cardRef: LegacyRef<HTMLDivElement>;
  unitLimitFree: number;
  unitMinEnterprise: number;
};

const PlanCardsContainer = ({
  productKey,
  productPlans,
  recommendedPlanName,
  signupURL,
  contactURL,
  currencyISOCode,
  fontSize,
  cardRef,
  unitLimitFree,
  unitMinEnterprise
}: PlanCardsContainerProps) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const isDesktop = useDesktopMediaQuery();
  const [showModal, setShowModal] = useState(false);
  const [state] = usePriceFinderStore();

  return (
    <ComponentWrapper productPlansLength={productPlans.length}>
      <CardsContainer productPlansLength={productPlans.length} ref={cardRef}>
        {productPlans.map((productPlan, idx, productPlans) => {
          const {
            planName,
            signupURLSuffix,
            planFeatureMessages,
            signupUnitLimit
          } = productPlan;

          const isHighlighted = planName === recommendedPlanName;
          const isRightHighlighted =
            isDesktop &&
            ((isHighlighted && idx === 0) ||
              productPlans[idx + 1]?.planName === recommendedPlanName);

          const overSignupLimit =
            state.unitCount > (signupUnitLimit || DefaultPlanLimits.UNIT_LIMIT);

          let ctaButton = (
            <CTAButton
              highlighted={isHighlighted}
              disabled={overSignupLimit}
              onClickHandler={() => {
                const analyticsEvent = createAnalyticsEvent({
                  name: TRACKING_EVENT_NAMES.planCardCTAButtonClicked,
                  label: `${planName}-start-trial`,
                  userBoxValue: state.unitCount
                });
                analyticsEvent.fire();

                window.location.href = signupURL + (signupURLSuffix || '');
              }}
            >
              <FormattedMessage
                defaultMessage="Start trial"
                description="Default CTA button label."
                id="ctaButtonDefault"
              />
            </CTAButton>
          );

          switch (planName) {
            case SpecialPlanNames.FREE:
              ctaButton = (
                <CTAButton
                  highlighted={isHighlighted}
                  onClickHandler={() => {
                    if (state.unitCount > unitLimitFree) {
                      const analyticsEvent = createAnalyticsEvent({
                        name: TRACKING_EVENT_NAMES.unitLimitBreachModal
                      });
                      analyticsEvent.fire();

                      setShowModal(true);
                    } else {
                      const analyticsEvent = createAnalyticsEvent({
                        name: TRACKING_EVENT_NAMES.planCardCTAButtonClicked,
                        label: `${planName}-get-it-now`,
                        userBoxValue: state.unitCount
                      });
                      analyticsEvent.fire();

                      window.location.href =
                        signupURL + (signupURLSuffix || '');
                    }
                  }}
                >
                  <FormattedMessage
                    defaultMessage="Get it now"
                    description="Free CTA button label."
                    id="ctaButtonFree"
                  />
                </CTAButton>
              );
              break;

            case SpecialPlanNames.ENTERPRISE:
              ctaButton = (
                <CTAButton
                  highlighted={isHighlighted}
                  onClickHandler={() => {
                    const analyticsEvent = createAnalyticsEvent({
                      name: TRACKING_EVENT_NAMES.planCardCTAButtonClicked,
                      label: `${planName}-contact-sales`,
                      userBoxValue: state.unitCount
                    });
                    analyticsEvent.fire();

                    window.location.href = contactURL;
                  }}
                >
                  <FormattedMessage
                    defaultMessage="Contact sales"
                    description="Enterprise CTA button label."
                    id="ctaButtonEnterprise"
                  />
                </CTAButton>
              );
              break;
          }

          return (
            <PlanCard
              key={idx}
              planName={planName}
              rightHighlighted={isRightHighlighted}
              highlighted={isHighlighted}
              ctaButton={ctaButton}
              planFeatureMessages={planFeatureMessages}
              featureMessagePlanName={
                productPlans[idx - 1] && productPlans[idx - 1]?.planName
              }
              first={idx === 0}
              last={idx === productPlans.length - 1}
            >
              <PlanCardBody
                billingCycle={state.billingCycle}
                fontSize={fontSize}
                overSignupLimit={overSignupLimit}
                planName={planName}
                currencyISOCode={currencyISOCode}
                unitCount={state.unitCount}
                productKey={productKey}
                unitMinEnterprise={unitMinEnterprise}
                priceCalcIdx={idx}
                contactURL={contactURL}
              />
            </PlanCard>
          );
        })}
      </CardsContainer>
      <ModalRedirectConfirm
        freeLimit={unitLimitFree}
        showModal={showModal}
        setShowModal={setShowModal}
        ctaURL={signupURL + (productPlans[0]?.signupURLSuffix || '')}
      />
    </ComponentWrapper>
  );
};

const ComponentWrapper = styled.div<{
  productPlansLength: number;
}>`
  display: grid;
  ${deviceMediaQueries.desktop} {
    ${(props) =>
      props.productPlansLength <= 2 ? 'justify-content:center' : ''}
  }
`;

const CardsContainer = styled.div<{
  productPlansLength: number;
}>`
  margin-top: 16px;

  ${deviceMediaQueries.desktop} {
    display: grid;
    grid-template-columns: repeat(
      ${(props) => props.productPlansLength},
      minmax(0, ${(props) => (props.productPlansLength >= 3 ? '1fr' : '390px')})
    );
    grid-template-rows: 1fr;
    margin-top: 25px;
  }
`;

export { PlanCardsContainer };
